import axiosInstance from "./axiosInstance";
import { ApiResponse } from "./pointsService";
import { log, LogLevel } from "utils/LogUtil";
import {
  AdminSubscriptionListParams,
  AdminSubscriptionListResult,
  AdminSubscriptionStats,
  AdminPaymentsResult,
  AdminCancelResult,
  AdminRetryResult,
  AdminSubscriptionRow,
  GrantPremiumRequest,
} from "common/types/adminSubscription";

const BASE = "/api/v1/subscriptions/admin";

/** undefined/null 파라미터를 직렬화 전에 제거(서버에 "undefined" 문자열이 가지 않도록). */
const buildQuery = (params?: Record<string, unknown>): string => {
  if (!params) return "";
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== ""
  );
  if (entries.length === 0) return "";
  const sp = new URLSearchParams(
    entries.reduce(
      (acc, [k, v]) => ({ ...acc, [k]: String(v) }),
      {} as Record<string, string>
    )
  );
  return `?${sp.toString()}`;
};

/** 전체 구독자 목록 — { items, total } 페이지네이션. status/planCode 미설정 시 미전송. */
export const fetchAdminSubscriptions = async (
  params?: AdminSubscriptionListParams
): Promise<ApiResponse<AdminSubscriptionListResult>> => {
  try {
    const response = await axiosInstance.get<ApiResponse<AdminSubscriptionListResult>>(
      `${BASE}${buildQuery(params as Record<string, unknown>)}`
    );
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "구독자 목록 조회 실패:", error);
    return { error: 1, message: "구독자 목록을 가져오지 못했습니다" };
  }
};

/** 구독 통계(상태별 수 / comp / 활성 매출). */
export const fetchAdminSubscriptionStats = async (): Promise<
  ApiResponse<AdminSubscriptionStats>
> => {
  try {
    const response = await axiosInstance.get<ApiResponse<AdminSubscriptionStats>>(
      `${BASE}/stats`
    );
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "구독 통계 조회 실패:", error);
    return { error: 1, message: "구독 통계를 가져오지 못했습니다" };
  }
};

/** 특정 구독 결제 이력. comp는 빈 목록. */
export const fetchAdminSubscriptionPayments = async (
  id: number,
  params?: { limit?: number; offset?: number }
): Promise<ApiResponse<AdminPaymentsResult>> => {
  try {
    const response = await axiosInstance.get<ApiResponse<AdminPaymentsResult>>(
      `${BASE}/${id}/payments${buildQuery(params as Record<string, unknown>)}`
    );
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "구독 결제이력 조회 실패:", error);
    return { error: 1, message: "결제 이력을 가져오지 못했습니다" };
  }
};

/** 강제 해지(구독 취소). comp는 EXPIRED, 유료는 CANCELLED로 응답. */
export const cancelAdminSubscription = async (
  id: number,
  reason?: string
): Promise<ApiResponse<AdminCancelResult>> => {
  try {
    const response = await axiosInstance.post<ApiResponse<AdminCancelResult>>(
      `${BASE}/${id}/cancel`,
      { reason }
    );
    return response.data;
  } catch (error: any) {
    log(LogLevel.ERROR, "구독 강제 해지 실패:", error);
    if (error.response?.data) return error.response.data;
    return { error: 1, message: "구독 해지에 실패했습니다" };
  }
};

/** 구독 복구(PortOne 재조회 동기화). */
export const retryAdminSubscription = async (
  id: number
): Promise<ApiResponse<AdminRetryResult>> => {
  try {
    const response = await axiosInstance.post<ApiResponse<AdminRetryResult>>(
      `${BASE}/${id}/retry`
    );
    return response.data;
  } catch (error: any) {
    log(LogLevel.ERROR, "구독 복구 실패:", error);
    if (error.response?.data) return error.response.data;
    return { error: 1, message: "구독 복구에 실패했습니다" };
  }
};

/** 무료 프리미엄 제공(comp). email 또는 userId 중 하나 필수. 결제창 불필요. */
export const grantPremium = async (
  request: GrantPremiumRequest
): Promise<ApiResponse<AdminSubscriptionRow>> => {
  try {
    const response = await axiosInstance.post<ApiResponse<AdminSubscriptionRow>>(
      `${BASE}/grant`,
      request
    );
    return response.data;
  } catch (error: any) {
    log(LogLevel.ERROR, "무료 프리미엄 제공 실패:", error);
    if (error.response?.data) return error.response.data;
    return { error: 1, message: "무료 프리미엄 제공에 실패했습니다" };
  }
};
